/**
 * Template assignment (owner rulings 2026-10-19): a template applies to
 * EVERY store unless it is assigned to chosen districts and/or stores.
 * District picks FOLLOW the district — a store that joins it picks the
 * template up, a store that leaves drops it (its old audits stay).
 *
 * Stored on every AuditTemplates row of the family as JSON in AssignedTo:
 *   {"v":1,"districts":["north"],"stores":["101"]}
 * districts trimmed + lower case, store numbers trimmed + lower case, so
 * the flows can compare with toLower(trim(...)). Blank = all stores.
 * Pure; shared by the app, the server and mirrored by Flows 1 + 4.
 */

export interface TemplateAssignment {
  /** District names, trimmed + lower case. */
  districts: string[];
  /** Store numbers, trimmed + lower case. */
  stores: string[];
}

/** Every store. */
export const ALL_STORES: TemplateAssignment = { districts: [], stores: [] };

const norm = (v: unknown): string => (typeof v === 'string' ? v.trim().toLowerCase() : '');
const uniq = (xs: string[]) => [...new Set(xs.filter(Boolean))];

/**
 * The stored column → an assignment. Blank, unreadable or empty = ALL
 * stores (the template's behaviour before assignments existed); the flows
 * treat a value that isn't a JSON object the same way.
 */
export function parseAssignment(raw: unknown): TemplateAssignment {
  if (typeof raw !== 'string' || !raw.trim().startsWith('{')) return ALL_STORES;
  try {
    const o = JSON.parse(raw) as { districts?: unknown; stores?: unknown };
    const list = (x: unknown) => (Array.isArray(x) ? uniq(x.map(norm)) : []);
    return { districts: list(o.districts), stores: list(o.stores) };
  } catch {
    return ALL_STORES;
  }
}

/** For the column: '' = all stores. */
export function serializeAssignment(a: TemplateAssignment): string {
  const districts = uniq(a.districts.map(norm)).sort();
  const stores = uniq(a.stores.map(norm)).sort();
  if (districts.length === 0 && stores.length === 0) return '';
  return JSON.stringify({ v: 1, districts, stores });
}

export const isAllStores = (a: TemplateAssignment) => a.districts.length === 0 && a.stores.length === 0;

/** Does the template apply to this store? */
export function appliesTo(a: TemplateAssignment, store: { storeNumber: string; district: string }): boolean {
  if (isAllStores(a)) return true;
  return a.stores.includes(norm(store.storeNumber)) || (norm(store.district) !== '' && a.districts.includes(norm(store.district)));
}

/** "All stores" / "2 districts + 3 stores" for lists. */
export function describeAssignment(a: TemplateAssignment): string {
  if (isAllStores(a)) return 'All stores';
  const parts = [
    a.districts.length ? `${a.districts.length} district${a.districts.length === 1 ? '' : 's'}` : '',
    a.stores.length ? `${a.stores.length} store${a.stores.length === 1 ? '' : 's'}` : '',
  ].filter(Boolean);
  return parts.join(' + ');
}

/** A district was renamed: assignments follow it (Admin → Districts rename). */
export function renameDistrictInAssignment(a: TemplateAssignment, from: string, to: string): TemplateAssignment {
  const f = norm(from);
  if (!a.districts.includes(f)) return a;
  return { ...a, districts: uniq(a.districts.map((d) => (d === f ? norm(to) : d))) };
}
